import type { SvgIconProps } from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import CakeIcon from '@mui/icons-material/Cake';
import SpaIcon from '@mui/icons-material/Spa';
import BrushIcon from '@mui/icons-material/Brush';
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import TheaterComedyIcon from '@mui/icons-material/TheaterComedy';
import CelebrationIcon from '@mui/icons-material/Celebration';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PlumbingIcon from '@mui/icons-material/Plumbing';
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices';
import ConstructionIcon from '@mui/icons-material/Construction';
import CarpenterIcon from '@mui/icons-material/Carpenter';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import SoupKitchenIcon from '@mui/icons-material/SoupKitchen';

type Match = { test: RegExp; icon: SvgIconComponent };

// Order matters — first match wins. More specific patterns (e.g. the new
// taxonomy codes) are evaluated before the legacy single-word patterns.
const RULES: Match[] = [
  // Senegal market — trades & home services (matched first; specific).
  { test: /plumb|plomb/i, icon: PlumbingIcon },
  { test: /electric|électric/i, icon: ElectricalServicesIcon },
  { test: /mason|maçon|macon|brick|construction/i, icon: ConstructionIcon },
  { test: /carpenter|menuis|woodwork/i, icon: CarpenterIcon },
  { test: /clean|ménage|menage|housekeep/i, icon: CleaningServicesIcon },
  { test: /child|garde[_\s]*d|nounou|nanny|babysit/i, icon: ChildCareIcon },
  { test: /cooking|home[_\s]*cook|cuistot|chef/i, icon: SoupKitchenIcon },
  { test: /decorat|décor/i, icon: CelebrationIcon },
  // Launch-taxonomy codes (matched first so they win over generic words).
  { test: /media[_\s]*photo|animation|photo|video|film|content/i, icon: CameraAltIcon },
  { test: /artist|performance|spectacle|dj|dancer|musician/i, icon: TheaterComedyIcon },
  { test: /event[_\s]*service|event_services|event\b/i, icon: CelebrationIcon },
  { test: /additional[_\s]*service|additional_services|other/i, icon: MoreHorizIcon },
  { test: /fashion[_\s]*style|fashion_style/i, icon: CheckroomIcon },
  // Existing patterns.
  { test: /braid|tress|locks|loc|natural/i, icon: ContentCutIcon },
  { test: /hair|coiff|wig|perr/i, icon: ContentCutIcon },
  { test: /cake|patiss|pastry|gateau|gâteau/i, icon: CakeIcon },
  { test: /food|cuisine|restau|catering|traiteur/i, icon: RestaurantIcon },
  { test: /makeup|maquill|lash|brow|sourcil/i, icon: BrushIcon },
  { test: /nail|ongle|manicure|pedicure/i, icon: BrushIcon },
  { test: /skin|peau|facial|spa|massage/i, icon: SpaIcon },
  { test: /beauty|beaut/i, icon: FaceRetouchingNaturalIcon },
  { test: /fashion|cloth|wear|couture|mode|tailor/i, icon: CheckroomIcon },
];

export function getCategoryIcon(input: { code?: string; name?: string }) {
  const haystack = `${input.code ?? ''} ${input.name ?? ''}`;
  for (const { test, icon } of RULES) {
    if (test.test(haystack)) return icon;
  }
  return StorefrontIcon;
}

type Props = SvgIconProps & {
  code?: string;
  name?: string;
};

export function CategoryIcon({ code, name, ...rest }: Props) {
  const Icon = getCategoryIcon({ code, name });
  return <Icon {...rest} />;
}
